import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { catchError, Observable, of, share, tap, throwError } from 'rxjs';
import { DefaultValueConstants } from '../../../../app/shared/constants/default-value-constants';
import { UserProfileConstants } from '../user-profile-constants';
import { UserService } from '../user.service';
import { UserProfileInformation } from '../models/user-profile-information';
import { formatDate } from '@angular/common';

@Component({
  selector: 'app-user-profile-slider',
  templateUrl: './user-profile-slider.component.html',
  styleUrls: ['./user-profile-slider.component.scss']
})
export class UserProfileSliderComponent {

  userProfileForm!: FormGroup;
  errorMessages: any = [];
  mandatoryFields = this.getDefaultMandatoryFieldsObject();
  @Output() closeUserProfileSlider = new EventEmitter<boolean>();
  sliderHeaderText:string= UserProfileConstants.UpdateUserProfileHeader;
  isEditUserProfile: boolean = false;
  eQMSScopeList: any = [];
  valueField: string = 'value';
  labelField: string = 'name';
  valueFieldForPuid: string = 'puid'
  usersList: any = [];
  userListObservable!:Observable<any>;
  minDateValue: Date = new Date();
  securityPermissions:any=[];
  userPuidForEdit:any;
  @Input() isUserProfileSliderOpen!: boolean;
  @Input() userPUIDForEdit!: any;
  userProfileInfoForEdit:any;
  infoForTerminationDate:string=UserProfileConstants.Info_For_Termination_Date;
  iseQMSScopeRequired: boolean = false;
  eQMSScopesForPrimaryScopeList: any = [];

  
  constructor(private formBuilder: FormBuilder,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private userService: UserService, private cdr: ChangeDetectorRef) {
    
  }
  ngOnInit()
  {
    this.userProfileForm=this.createFormGroupWithBuilderAndModel();
    this.initialize();
    this.getUsers();
    this.getSecurityPermissions();
  }

  createFormGroupWithBuilderAndModel() {
    return this.formBuilder.group({
      PrimaryeQMSScope: new FormControl(null, [Validators.required]),
      OthereQMSScopes: new FormControl(),
      manager: new FormControl(null),
      securityPermissions: new FormControl(null, [Validators.required]),
      hireDate: new FormControl(null, [Validators.required]),
      terminationDate: new FormControl(),
    })
  }


  confirmClose() {
    this.confirmationService.confirm({
      message: UserProfileConstants.User_Profile__Confirmation_Text,
      header: UserProfileConstants.User_Profile_Slider_Confirmation_Header,
      icon: "pi pi-exclamation-triangle",
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.closeAndResetUserProfileSlider();
      }
    });
  }

  onTerminationDateChange(){
    if(this.userProfileForm.controls['terminationDate'].getRawValue()!=null ||this.userProfileForm.controls['terminationDate'].getRawValue()!=undefined)
    {
      this.iseQMSScopeRequired = false;
    }
    else
    {
      this.iseQMSScopeRequired = true;
    }
    
  }

  closeAndResetUserProfileSlider() {
    this.closeUserProfileSlider.emit(true);
    this.isUserProfileSliderOpen=false;
    this.userProfileForm.reset();
    this.errorMessages = [];
    this.mandatoryFields = this.getDefaultMandatoryFieldsObject();
    this.isEditUserProfile=false;
    this.sliderHeaderText=UserProfileConstants.UpdateUserProfileHeader;

  }

  private getDefaultMandatoryFieldsObject() {
    return {
      securityPermissions: false,
      PrimaryeQMSScope:false,
      hireDate:false
    };
  }

  initialize() {
    this.userService.geteQMSScopesForUserProfile().subscribe(
      (eQMSscopeData) => {
        this.eQMSScopeList = eQMSscopeData.map((dataItem: any) => ({
          value: dataItem.scopeId,
          name: dataItem.scopeName
        }));
        this.eQMSScopesForPrimaryScopeList = eQMSscopeData
          .filter((dataItem: any) => dataItem.isAvailableInITAAG)
          .map((dataItem: any) => ({
            value: dataItem.scopeId,
            name: dataItem.scopeName
          }));
      });
  }

getUsers(){
  this.userService.getAllUsers().subscribe((usersData)=>{
    this.usersList=usersData;
  })
}

getSecurityPermissions(){
  this.userService.getSecurityPermissions().subscribe((securityPermissions)=>{
    this.securityPermissions=securityPermissions;
  })
}

getUserProfileDataForEdit(puid:any){
  this.userPuidForEdit=puid;
  this.userService.getUserProfileInformationForEdit(puid).subscribe((userProfileInfo)=>{
    this.userProfileInfoForEdit=userProfileInfo;
    this.populateUserProfileDetailsForEdit(this.userProfileInfoForEdit);
  })
}


private populateUserProfileDetailsForEdit(data: any) {
  const hireDateInfo = formatDate(data.hireDate, "dd-MMM-yyyy", "en-US")
  const terminationDateInfo = formatDate(data.terminationDate, "dd-MMM-yyyy", "en-US")
  this.iseQMSScopeRequired= data.terminationDate===null|| data.terminationDate === undefined;
  this.userProfileForm.patchValue({
    PrimaryeQMSScope: data.primaryeQMSScope,
    OthereQMSScopes: data.othereQMSScope,
    manager: data.manager?.puid,
    securityPermissions: data.securityPermissions,
    hireDate: new Date(hireDateInfo),
    terminationDate:data.terminationDate!=null|| data.terminationDate!= undefined? new Date(terminationDateInfo):null 
  }, { emitEvent: false, onlySelf: true });
}


UpdateUserProfile(){
  this.validateUserProfileForm();
  if (this.errorMessages.length == 0) {
    let updatedProfileInfo = this.mapUserProfileFormToUserProfile();
    this.userService.updateUserProfile(this.userPuidForEdit, updatedProfileInfo)
    .pipe(
      catchError((err) => {
        return throwError(() => err);
      })
    )
    .subscribe((response) => {
        this.messageService.add({
            severity: DefaultValueConstants.Severity_Success,
            summary: UserProfileConstants.User_Profile_Info_Updated_Success
        });
        this.closeAndResetUserProfileSlider();
        this.cdr.detectChanges();
    });
  }
  this.cdr.detectChanges();
}

mapUserProfileFormToUserProfile():UserProfileInformation {

  let formValues = this.userProfileForm.getRawValue();
  var managerInfo = (formValues.manager !== null && formValues.manager !== undefined) ? this.usersList.find((item:any) => item.puid == formValues.manager) : null;

  let updateUserProfile : UserProfileInformation = {
    primaryeQMSScope: formValues.PrimaryeQMSScope,
    othereQMSScope: formValues.OthereQMSScopes,
    manager: (managerInfo !== null && managerInfo !== undefined) ? {name:managerInfo?.name, puid:managerInfo?.puid} : null,
    securityPermissions: formValues.securityPermissions,

    hireDate: new Date(Date.UTC(
        new Date(formValues.hireDate).getFullYear(),
        new Date(formValues.hireDate).getMonth(),
        new Date(formValues.hireDate).getDate()
      )),
    terminationDate: (formValues.terminationDate !== null  && formValues.terminationDate !== undefined) ? new Date(Date.UTC(
      new Date(formValues.terminationDate).getFullYear(),
      new Date(formValues.terminationDate).getMonth(),
      new Date(formValues.terminationDate).getDate()
    )) : null
  };

  return updateUserProfile;
}


private validateUserProfileForm() {
  this.errorMessages = [];
  this.mandatoryFields = this.getDefaultMandatoryFieldsObject();
  if(this.userProfileForm.controls['terminationDate'].getRawValue()!=null ||this.userProfileForm.controls['terminationDate'].getRawValue()!=undefined )
  {
    delete (this.mandatoryFields as any).PrimaryeQMSScope;
  }
  if (this.userProfileForm.invalid) {
    Object.keys(this.mandatoryFields).forEach(key => {
      if (this.userProfileForm.get(key)?.errors) {
        let errorText = this.getErrorMessageText(key);
        this.errorMessages.push(errorText);
      }
    });
  }
}


private getErrorMessageText(labelName: string) {
  let errorText: string = "";
  switch (labelName) {
    case 'PrimaryeQMSScope':
        errorText = UserProfileConstants.UserProfile_Primary_eQMSScope_Required;
        this.mandatoryFields.PrimaryeQMSScope = true;    
      break;
    case 'securityPermissions':
      errorText = UserProfileConstants.UserProfile_Security_Permissions_Required;
      this.mandatoryFields.securityPermissions = true;
      break;
    case 'hireDate':
      errorText = UserProfileConstants.UserProfile_Hire_Date_Required;
      this.mandatoryFields.hireDate = true;
      break;
    
  }
  return errorText;
}
  
}
